import { useState, useCallback, useEffect } from 'react'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'

import { SetState, WorkspaceSettings } from 'types'
import { reportError } from 'utils/error'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import client from 'api/client'
import { ActionableInput } from '../../../components/ActionableInput/ActionableInput'
import { truncate } from 'lodash'
import InfoModal from 'components/InfoModal/InfoModal'
import Styles from './ScimTokenInput.module.scss'

interface Props {
  workspace?: WorkspaceSettings
  isSaving: boolean
  setIsSaving: SetState<boolean>
  isDisabled?: boolean
}

export const ScimTokenInput = ({ workspace, isSaving, setIsSaving, isDisabled }: Props) => {
  const [scimToken, setScimToken] = useState(workspace?.scim_token || '')
  const [confirmRefreshVisible, setConfirmRefreshVisible] = useState(false)
  const workspaceKey = workspace?.key

  useEffect(() => {
    if (workspace) {
      setScimToken(workspace.scim_token)
    }
  }, [workspace])

  const handleRefreshScimToken = useCallback(async () => {
    if (isSaving || !workspace) return

    setConfirmRefreshVisible(false)

    setIsSaving(true)

    const res = await client.refreshScimToken()

    if (res.type === SUCCESS_RESPONSE_TYPE) {
      setScimToken(res.data.token)
      showToastMessage('Your SCIM token has been refreshed!', 'success')
    } else {
      showToastMessage("Couldn't regenerate your SCIM token. Please try again later", 'error')
      reportError('Failed to update saml settings', {
        severity: 'error',
        metaData: { workspaceKey },
      })
    }

    setIsSaving(false)
  }, [isSaving, setIsSaving, workspaceKey, workspace])

  const handleCopyScimToken = useCallback(() => {
    navigator.clipboard.writeText(scimToken)
    showToastMessage('SCIM Token copied to your clipboard', 'success')
  }, [scimToken])

  return (
    <>
      <InfoModal
        onOk={() => {
          handleRefreshScimToken()
          return true
        }}
        okText="Yes, refresh"
        cancelText="No, cancel"
        title="Please confirm..."
        visible={confirmRefreshVisible}
        setVisible={setConfirmRefreshVisible}
      >
        Are you sure you want to refresh your SCIM token?
        <div className={Styles.warning}>
          <i>This will block SCIM requests to your workspace until the token is updated in your IdP</i>
        </div>
      </InfoModal>
      <ActionableInput
        isDisabled={isDisabled}
        label="SCIM Token"
        value={truncate(scimToken, { length: 60 })}
        actions={[
          { title: 'Refresh', onClick: () => setConfirmRefreshVisible(true) },
          { title: 'Copy', onClick: handleCopyScimToken },
        ]}
      />
    </>
  )
}
